import { useMemo } from 'react'
import { BigNumber } from 'bignumber.js'
import useWeb3Provider from 'hooks/useActiveWeb3React'
import { useDeserializedFarmUserFromPid } from 'state/farms/useDeserializedFarmsUser'
import { useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useBoostableFarmInfo } from 'state/farmBoost/useBoostableFarmsInfo'
import { useUserBoostedFarmsInfo } from 'state/farmBoost/useUserBoostedFarmsInfos'
import getBoostFactor from 'utils/getBoostFactor'
import { BIG_ZERO } from 'utils/bigNumber'
import { DEFAULT_TOKEN_DECIMAL } from 'config'

export interface FarmBoostFactorResult {
  loaded: boolean
  baseAllocation: BigNumber
  boostAllocation: BigNumber
  adjustShares: BigNumber
  boostFactor: number
}
const LOADING_FARM_BOOST_FACTOR_RESULT: FarmBoostFactorResult = {
  loaded: false,
  baseAllocation: undefined,
  boostAllocation: undefined,
  adjustShares: undefined,
  boostFactor: undefined,
}

export const useFarmBoostFactor = (farmId?: number): FarmBoostFactorResult => {
  const farmVersion = getFarmVersionFromPid(farmId)

  const { account } = useWeb3Provider()

  const farmUserData = useDeserializedFarmUserFromPid(farmId, farmVersion)
  const farm = useDeserializedFarmFromPid(farmId, farmVersion)
  const boostAllocation = useBoostableFarmInfo(farmId)?.boostAllocation
  const [userBoostedFarmInfo] = useUserBoostedFarmsInfo(farmId)

  const { allocPoint: baseAllocation, lpTotalStaked } = farm ?? {}
  const userFarmPosition = account ? farmUserData?.stakedBalance : BIG_ZERO

  return useMemo(() => {
    const adjustShares = userBoostedFarmInfo?.actualAdjustedShare || BIG_ZERO

    if ((account && !farmUserData?.loaded) || !(userFarmPosition && baseAllocation && boostAllocation && lpTotalStaked))
      return LOADING_FARM_BOOST_FACTOR_RESULT

    return {
      loaded: true,
      baseAllocation,
      boostAllocation,
      adjustShares: adjustShares.div(DEFAULT_TOKEN_DECIMAL),
      boostFactor: getBoostFactor(userFarmPosition, baseAllocation, boostAllocation, lpTotalStaked, adjustShares),
    }
  }, [account, farmUserData, userFarmPosition, baseAllocation, boostAllocation, lpTotalStaked, userBoostedFarmInfo])
}
